'use client';

import { useRef, type ReactNode, type ElementType, type ComponentPropsWithoutRef } from 'react';
import { useInView } from 'framer-motion';

type RevealOnScrollProps<T extends ElementType> = {
  as?: T;
  className?: string;
  children?: ReactNode;
} & Omit<ComponentPropsWithoutRef<T>, 'as' | 'className' | 'children'>;

/**
 * Applies the legacy .reveal/.reveal-left/.reveal-right/.reveal-side CSS
 * animations (defined in globals.css) by toggling `.active` once the
 * element scrolls into view — same threshold/rootMargin as the original
 * IntersectionObserver, driven by Framer Motion's useInView instead.
 */
export default function RevealOnScroll<T extends ElementType = 'div'>({
  as,
  className = '',
  children,
  ...rest
}: RevealOnScrollProps<T>) {
  const Component = (as || 'div') as ElementType;
  const ref = useRef<HTMLElement>(null);
  const isInView = useInView(ref as React.RefObject<Element>, {
    once: true,
    amount: 0.15,
    margin: '0px 0px -50px 0px',
  });

  return (
    <Component ref={ref} className={`${className}${isInView ? ' active' : ''}`} {...rest}>
      {children}
    </Component>
  );
}
